import { consola } from 'consola';

/**
 * Provides utility functions to interact with the browser's localStorage,
 * including support for setting and retrieving items with expiration (TTL).
 */
export const useStorage = () => {
  const nuxtApp = tryUseNuxtApp();
  const defaultWindow = typeof window !== 'undefined';

  // Helper function for localStorage warnings
  const logLocalStorageWarning = (action: string, details: string) => {
    // Only log warnings in development
    if (useRuntimeConfig().public.isDev) {
      if (nuxtApp?.$logWarning) {
        nuxtApp.$logWarning(
          'localStorage not available (server-side or disabled):',
          `useStorage/${action}`,
          details
        );
      } else {
        consola.warn(
          'localStorage not available (server-side or disabled):',
          `useStorage/${action}`,
          details
        );
      }
    }
  };

  const logError = (error: Error, context: string, details: string) => {
    if (nuxtApp?.$logError) {
      nuxtApp.$logError(error, context, details);
    } else {
      consola.error(error, context, details);
    }
  };

  /**
   * Retrieves an item from localStorage, automatically deserializing JSON objects.
   *
   * @param {string} key The key of the item to retrieve.
   * @returns {any | undefined} The value of the item, or undefined if not found.
   */
  const getItem = (key: string): any | undefined => {
    if (defaultWindow) {
      try {
        const item = localStorage.getItem(key);
        if (item === null) {
          return undefined;
        }
        return JSON.parse(item);
      } catch (error) {
        logError(
          error as Error,
          'useStorage/getItem',
          `Failed to parse stored item with key ${key}:`
        );
        window.localStorage.removeItem(key); // Optionally, remove the invalid item
        return undefined;
      }
    } else {
      // Server-side or localStorage not available - log warning
      logLocalStorageWarning('getItem', `Cannot retrieve item with key ${key}`);
      return undefined;
    }
  };

  /**
   * Stores an item in localStorage, automatically serializing objects to JSON.
   *
   * @param {string} key The key under which to store the item.
   * @param {any} value The value to be stored, can be an object or string.
   */
  const setItem = (key: string, value: any): void => {
    // const { logError } = useNuxtApp();
    if (defaultWindow) {
      try {
        const item = JSON.stringify(value);
        localStorage.setItem(key, item);
      } catch (error) {
        logError(
          error as Error,
          'useStorage/setItem',
          `Failed to serialize and store item with key ${key}:`
        );
      }
    } else {
      logLocalStorageWarning('setItem', `Cannot store item with key ${key}`);
    }
  };

  /**
   * Removes an item from localStorage.
   *
   * @param {string} key The key of the item to remove.
   */
  const removeItem = (key: string): void => {
    if (defaultWindow) {
      localStorage.removeItem(key);
    } else {
      logLocalStorageWarning(
        'removeItem',
        `Cannot remove item with key ${key}`
      );
    }
  };

  /**
   * Stores an item in localStorage with an expiration time (TTL).
   * Serializes the item to JSON including the expiration time.
   *
   * @param {string} key The key under which to store the item.
   * @param {any} value The value to be stored, can be an object or string.
   * @param {number} ttl The time-to-live (TTL) in milliseconds.
   */
  const setItemWithTTL = (key: string, value: any, ttl: number): void => {
    const expiresAt = Date.now() + ttl;
    const item = JSON.stringify({ value, expiresAt });
    setItem(key, item);
  };

  /**
   * Retrieves an item from localStorage that has an expiration time.
   * If the item has expired, it is automatically removed.
   *
   * @param {string} key The key of the item to retrieve.
   * @returns {any | null} The value of the item, or null if expired or not found.
   */
  const getItemWithExpiry = (key: string): any | null => {
    const itemStr = getItem(key);
    if (!itemStr) return null;

    const { value, expiresAt } = itemStr;
    if (Date.now() > expiresAt) {
      removeItem(key);
      return null;
    }
    return value;
  };

  /**
   * Removes all items from localStorage that have passed their expiration time.
   * This function iterates over every item in localStorage, checks if it contains an
   * expiration time (`expiresAt`), and if the current time is past this expiration time,
   * the item is removed. This is useful for cleaning up expired data and preventing
   * localStorage from being cluttered with obsolete items. The function only operates if
   * the window object is available to ensure compatibility with environments where
   * localStorage might not be accessible.
   */
  const removeAllExpired = (): void => {
    if (defaultWindow) {
      Object.keys(localStorage).forEach((key) => {
        const itemStr = getItem(key);
        if (itemStr && typeof itemStr === 'object' && 'expiresAt' in itemStr) {
          if (Date.now() > itemStr.expiresAt) {
            removeItem(key);
          }
        }
      });
    } else {
      logLocalStorageWarning('removeAllExpired', 'Cannot remove expired items');
    }
  };

  return {
    getItem,
    setItem,
    removeItem,
    setItemWithTTL,
    getItemWithExpiry,
    removeAllExpired
  };
};

// TODO: Namespaced Storage
// 2. **Namespaced Storage**:
//    Allow creating a namespaced instance of the storage for different parts/modules of your app.

//    ```javascript
//    export const useNamespacedStorage = (namespace: string) => {
//      const namespacedKey = (key: string) => `${PREFIX}.${namespace}.${key}`;
//      // ... rest of the logic with namespacedKey
//    };
//    ```
// TODO: Listen for Changes:
// 3. **Listen for Changes:**:
//    Listen for storage changes in real-time, useful if you have multiple tabs or windows and want to keep data in sync.

//    ```javascript
//    const onChange = (callback: Function) => {
//      if (defaultWindow) {
//        window.addEventListener('storage', (event) => {
//          if (event.key?.startsWith(PREFIX)) {
//            callback(event);
//          }
//        });
//      }
//    };
//    ```

// 4. **JSON Support**:
//    Allow storing and retrieving objects seamlessly.

//    ```javascript
//    const setJSON = (key: string, value: any) => {
//      if (defaultWindow) {
//        localStorage.setItem(PREFIX + key, JSON.stringify(value));
//      }
//    };

//    const getJSON = (key: string) => {
//      const storedValue = getItem(key);
//      return storedValue ? JSON.parse(storedValue) : null;
//    };
//    ```

// 5. **Size of Storage**:
//    Provide a function that tells the user how much storage space is left.

//    ```javascript
//    const storageLeft = () => {
//      if (defaultWindow) {
//        return 5 * 1024 * 1024 - encodeURIComponent(JSON.stringify(localStorage)).length;
//      }
//      return 0;
//    };
//    ```

// 6. **Clear All Namespaced Items**:
//    If you implement namespacing, it can be useful to clear all items from a specific namespace.

//    ```javascript
//    const clearNamespace = (namespace: string) => {
//      for (const key in localStorage) {
//        if (key.startsWith(`${PREFIX}.${namespace}`)) {
//          localStorage.removeItem(key);
//        }
//      }
//    };
//    ```

// 7. **Backup and Restore**:
//    Functions to export all items to a JSON blob (useful for backup) and restore from a blob.
